import * as React from 'react';
import { IWebgroupSearchResultsData } from './webgroup-search-results.data';
import { IWebgroupSearchResultsProps } from './webgroup-search-results.props.autogenerated';
import { SearchCategory } from './components/search-category-results';
import { SearchProduct } from './components/search-product-results';
import {
    flattenCategories,
    GetCategoryIdFromUrl,
    getCategoryParents,
    GetCurrentCategory,
    GetQeuryFromUrl,
    GetSearchTermFromUrl,
    getAllProductsCategoryRecId
} from '../../utilities';
import { IWebGroupSearchResultEntity } from '../../actions/DataServiceEntities.g';
import Msdyn365 from '@msdyn365-commerce/core';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

export interface IWebgroupSearchResultsViewProps extends IWebgroupSearchResultsProps<IWebgroupSearchResultsData> {
    searchCategories?: React.ReactNode;
    searchProducts?: React.ReactNode;
    query?: string;
    searchTerm?: string;
    categoryId?: string;
    categoryName?: string;
    breadcrumb?: React.ReactNode;
}

export interface IWebGroupSearchItem extends IWebGroupSearchResultEntity {
    id: string | number | undefined;
    image: string;
    url: string;
}
/**
 *
 * WebgroupSearchResults component
 * @extends {React.PureComponent<IWebgroupSearchResultsProps<IWebgroupSearchResultsData>>}
 */
class WebgroupSearchResults extends React.PureComponent<IWebgroupSearchResultsViewProps> {
    private _renderCategories = (categories: IWebGroupSearchItem[]): React.ReactNode => {
        if (categories && categories.length) {
            return categories.map(c => {
                const categoryName = GetCurrentCategory(this.props.data.categories.result ?? [], c.CategoryId)?.Name?.replace(
                    /\s?\(\d*\)$/,
                    ''
                );
                return (
                    <SearchCategory
                        Name={c.Name}
                        WebGroupId={c.WebGroupId}
                        config={this.props.config}
                        context={this.props.context.actionContext}
                        key={c.CategoryId}
                        id={c.CategoryId}
                        CategoryId={c.CategoryId}
                        title={categoryName ?? c.Name ?? ''}
                        url={this._goToPage(c.CategoryId)}
                    />
                );
            });
        }
        return <></>;
    };

    private _renderProducts = (products: IWebGroupSearchItem[], query: string): React.ReactNode => {
        const { resources } = this.props;
        const { matchedProductsText } = resources;
        if (products && products.length) {
            return (
                <div>
                    <h2 className='title'>
                        {products.length} {matchedProductsText}
                    </h2>
                    {products.map(p => {
                        const url = this._goToPage(p.CategoryId, p.WebGroupId) + (query ? `&search_term=${query}` : '');
                        return (
                            <SearchProduct
                                config={this.props.config}
                                context={this.props.context.actionContext}
                                Header={p.Header}
                                title={(p.Name ?? '').replace(/\s?\(\d*\)$/, '')}
                                id={p.WebGroupId}
                                key={p.WebGroupId}
                                CategoryId={0}
                                url={url}
                            />
                        );
                    })}
                </div>
            );
        }
        return;
    };

    private _goToPage = (categoryId: any, webgroupId?: string): string => {
        const { context } = this.props;
        const { request } = context;
        const url = new URL(`${request.url.requestUrl.origin}/${this.props.config.pageName}`);
        url.searchParams.append('categoryId', categoryId);
        if (webgroupId) {
            url.searchParams.append('webgroupId', webgroupId);
        }
        return url.href;
    };

    private _renderBreadCrumbLink = (category: any): React.ReactNode => {
        const {
            context: { request }
        } = this.props;
        if (Msdyn365.isBrowser && category) {
            const url = new URL(`${request.url.requestUrl.origin}/${this.props.config.pageName}`);
            url.searchParams.append('categoryId', category.RecordId);
            return (
                <a className='breadcrum-link' href={url.href}>
                    {category.Name.replace(/\s?\(\d*\)$/, '')}
                </a>
            );
        }
        return <></>;
    };

    private _renderBreadcrumb = (currentCategory: CategoryHierarchy | undefined, categories: CategoryHierarchy[]): React.ReactNode => {
        let parents: CategoryHierarchy[] = [];
        let links = [];
        if (currentCategory && categories.length) {
            parents = getCategoryParents(currentCategory, categories, parents).filter(i => i !== undefined);
        }
        const parsedLinks = parents.reverse();
        currentCategory && parsedLinks.push(currentCategory);
        links = parsedLinks.map((c: CategoryHierarchy, index: number) => {
            return this._renderBreadCrumbLink(c);
        });
        return links.length ? links : null;
    };

    public render(): JSX.Element | null {
        const {
            data: { webGroupSearchResult, categories },
            context: { actionContext },
            config: { allProductsCategoryRootName },
            resources: { defaultAllProductsCategoryRootName }
        } = this.props;
        const query = GetQeuryFromUrl(actionContext);
        let categoryId = GetCategoryIdFromUrl(actionContext);
        const allProductsCategoryId = getAllProductsCategoryRecId(
            categories?.result,
            allProductsCategoryRootName ?? defaultAllProductsCategoryRootName
        );
        const flattenedCategories = categoryId ? flattenCategories(categories?.result || []) : [];
        if (!query && !categoryId && allProductsCategoryId) {
            // set to All Products
            categoryId = allProductsCategoryId;
        }
        const parsedCategories: IWebGroupSearchItem[] = [];
        const products: IWebGroupSearchItem[] = [];
        const currentCategory = categories?.result && GetCurrentCategory(categories?.result, categoryId);
        if (currentCategory?.Children) {
            for (const cat of currentCategory.Children) {
                parsedCategories.push({ CategoryId: cat.RecordId, Name: cat.NeutralizedName } as IWebGroupSearchItem);
            }
        } else {
            webGroupSearchResult?.result?.map((i: IWebGroupSearchResultEntity) => {
                const check = parsedCategories.find(c => c.CategoryId === i.CategoryId);
                if (!check) {
                    parsedCategories.push({
                        CategoryId: i.CategoryId, //@ts-ignore
                        Name: i[`NeutralizedName`] ?? i.Name,
                        WebGroupId: i.WebGroupId
                    } as IWebGroupSearchItem);
                }
                products.push({
                    CategoryId: i.CategoryId,
                    WebGroupId: i.WebGroupId,
                    Header: i.Header,
                    Header2: i.Header2
                } as IWebGroupSearchItem);
            });
        }

        if (Msdyn365.isBrowser) {
            // redirect to webgroup detail if there's one result -- this indicates an exact product match
            if (parsedCategories.length === 1 && products.length === 1) {
                location.href = `${this._goToPage(parsedCategories[0].CategoryId, parsedCategories[0].WebGroupId)}&search_term=${query}`;
                return this.props.renderView(this.props);
            } else if (parsedCategories.length >= 1) {
                // redirect to webgroup if there is an exact webgroup result
                const exactWebgroupMatch = parsedCategories.find(category => category.WebGroupId === query);
                if (exactWebgroupMatch) {
                    location.href = `${this._goToPage(exactWebgroupMatch.CategoryId, parsedCategories[0].WebGroupId)}&search_term=${query}`;
                    return this.props.renderView(this.props);
                }
            }
        }
        const viewProps = {
            ...this.props,
            query: query,
            searchTerm: GetSearchTermFromUrl(actionContext),
            categoryName: currentCategory?.Name?.replace(/\s?\(\d*\)$/, ''),
            categoryId,
            searchProducts: this._renderProducts(products, query),
            searchCategories: this._renderCategories(parsedCategories),
            breadcrumb: this._renderBreadcrumb(currentCategory, flattenedCategories)
        };
        return this.props.renderView(viewProps);
    }
}

export default WebgroupSearchResults;
